
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSidenav } from '@angular/material/sidenav';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  Observable,
  Subscription,
  startWith,
  map,
  shareReplay
} from 'rxjs';

import { CoefficientNameItem } from '../../pump-simulation/coefficient.model';
import { DisplayPumpSimulationService } from '../../pump-simulation/display-pump-simulation.service';
import { filenameSort } from '../../shared/util';
import { PumpSimulationHelperService } from '../pump-simulation-helper.service';

import {
  setCoefficientsData,
  setSelectedCoefficientsData
} from '../store/calculate-coefficient/calculate-coefficient.actions';

import {
  setChartSettingsValue,
  setLoadSimulationStepper,
  setSimulationProcess
} from '../store/charts-setting/charts-setting.actions';

import {
  setCurrentMeasuredData,
  setCurrentCalculatedData
} from '../store/charts/current-chart/current-chart.actions';

import {
  setFlowRateMeasuredData,
  setFlowRateCalculatedData
} from '../store/charts/flow-rate-calculate/flow-rate-calculate.actions';

import {
  setIntermediateData,
  setIntermediateLoading
} from '../store/charts/intermediate-chart/intermediate-chart.actions';

import {
  setNPSHMeasuredData,
  setNPSHCalculatedData
} from '../store/charts/npsh-chart/npsh-chart.actions';

import {
  setRotationalMeasuredData,
  setRotationalCalculatedData
} from '../store/charts/rotational-speed-chart/rotational-speed-chart.actions';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {

  @ViewChild(MatSidenav) private sidenav!: MatSidenav;

  private subscriptions: Subscription[] = [];

  nameControl = new UntypedFormControl('');

  filteredOptions!: Observable<CoefficientNameItem[]>;
  coefficientSetNameList!: Observable<CoefficientNameItem[]>;

  options: CoefficientNameItem[] = [];
  selectedCoefficient: CoefficientNameItem | null = null;
  navTitle = '';

  constructor(
    private store: Store,
    private router: Router,
    private displayPumpSimulationService: DisplayPumpSimulationService,
    private pumpSimulationHelperService: PumpSimulationHelperService
  ) {}

  ngOnInit(): void {

    this.coefficientSetNameList =
      this.displayPumpSimulationService.getAllMeasurementFileVersions().pipe(
        map(data => {

          const unique = new Map<string, CoefficientNameItem>();

          for (const item of data) {
            // Only include version 0 of each coefficient set.
            if (Number(item.version) !== 0) {
              continue;
            }

            if (!unique.has(item.filename)) {
              unique.set(item.filename, {
                filename: item.filename,
                displayName:
                  !this.pumpSimulationHelperService.isEmpty(item.displayName)
                    ? item.displayName
                    : item.filename
              });
            }
          }

          const sorted = filenameSort(Array.from(unique.values()));

          this.options = sorted;
          this.updateNavTitle();

          return sorted;
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    this.filteredOptions = combineLatest([
      this.coefficientSetNameList,
      this.nameControl.valueChanges.pipe(startWith(''))
    ]).pipe(
      map(([options, value]) => this.filterOptions(options, value))
    );

    this.subscriptions.push(
      this.coefficientSetNameList.subscribe()
    );

    this.resetChartData();
  }

  private filterOptions(
    options: CoefficientNameItem[],
    value: string | CoefficientNameItem | null
  ): CoefficientNameItem[] {

    const searchText =
      typeof value === 'string'
        ? value
        : value?.displayName ?? '';

    const filterValue = searchText.trim().toLowerCase();

    if (!filterValue) {
      return options;
    }

    return options.filter(option =>
      option.filename.toLowerCase().includes(filterValue) ||
      option.displayName.toLowerCase().includes(filterValue)
    );
  }

  displaySimulationName(
    option: CoefficientNameItem | string | null
  ): string {

    if (!option) {
      return '';
    }

    return typeof option === 'string'
      ? option
      : option.displayName;
  }

  onSelectionChange(event: MatAutocompleteSelectedEvent): void {

    const selected = event.option.value as CoefficientNameItem;

    if (!selected?.filename) {
      return;
    }

    // Preserve both the filename and the display name.
    this.selectedCoefficient = selected;

    // Keep the selected display name visible in the search field.
    this.nameControl.setValue(selected, { emitEvent: false });

    this.navTitle = `Version overview: ${selected.displayName}`;

    this.sidenav.toggle();

    this.store.dispatch(
      setLoadSimulationStepper({ data: false })
    );

    this.router.navigate([
      '/simulations/simulation',
      selected.filename
    ]);
  }

  private updateNavTitle(): void {

    if (this.router.url === '/simulations') {
      this.navTitle = '';
      return;
    }

    if (this.router.url === '/simulations/simulation/new') {
      this.navTitle = 'Create Pump Simulation';

      this.store.dispatch(
        setSimulationProcess({ data: 'create' })
      );

      return;
    }

    const urlParts = this.router.url.split('/');
    const filename = decodeURIComponent(urlParts[urlParts.length - 1]);

    const selected = this.options.find(
      item => item.filename === filename
    );

    if (selected) {
      this.selectedCoefficient = selected;
      this.nameControl.setValue(selected, { emitEvent: false });
    }

    this.navTitle =
      `Version overview: ${selected?.displayName ?? filename}`;

    this.store.dispatch(
      setSimulationProcess({ data: 'load' })
    );
  }

  handleTitle(value: string): void {

    if (value === 'create') {

      this.selectedCoefficient = null;
      this.nameControl.setValue('');

      this.navTitle = 'Create Pump Simulation';

      this.store.dispatch(
        setSimulationProcess({ data: 'create' })
      );

    } else {

      const selected = this.options.find(
        item => item.filename === value
      );

      this.selectedCoefficient = selected ?? null;

      if (selected) {
        this.nameControl.setValue(selected, { emitEvent: false });
      }

      this.navTitle =
        `Version overview: ${selected?.displayName ?? value}`;

      this.store.dispatch(
        setSimulationProcess({ data: 'load' })
      );
    }

    this.sidenav.toggle();

    this.store.dispatch(
      setLoadSimulationStepper({ data: false })
    );
  }

  private resetChartData(): void {

    this.store.dispatch(setFlowRateMeasuredData({ data: null }));
    this.store.dispatch(setFlowRateCalculatedData({ data: null }));

    this.store.dispatch(setCurrentMeasuredData({ data: null }));
    this.store.dispatch(setCurrentCalculatedData({ data: null }));

    this.store.dispatch(setRotationalMeasuredData({ data: null }));
    this.store.dispatch(setRotationalCalculatedData({ data: null }));

    this.store.dispatch(setIntermediateData({ data: null }));
    this.store.dispatch(setIntermediateLoading({ data: true }));

    this.store.dispatch(setNPSHMeasuredData({ data: null }));
    this.store.dispatch(setNPSHCalculatedData({ data: null }));

    this.store.dispatch(setCoefficientsData({ data: null }));
    this.store.dispatch(setSelectedCoefficientsData({ data: null }));

    this.store.dispatch(setChartSettingsValue({ data: null }));
  }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }
}
